import * as SignalRClient from '@microsoft/signalr'
import _ from 'lodash'
import { baseUrls } from 'services/servicesBaseUrl'
import { GetAccessToken, ShowException } from 'store/Application/appActions'
import { store } from 'store/configureStore'
import { Debugger } from 'utils/debugger'
import {
  AddGroup,
  RemoveGroup,
  SetConnectionStatus,
} from '../store/SignalRStore'

export class SignalRService {
  private _connection: SignalRClient.HubConnection
  private hub = 'live-updates'
  private static _client: SignalRService
  private _currentConnectedGroups: string[] = []

  public static async GetHub() {
    if (!SignalRService._client) {
      const hub = new SignalRService()
      SignalRService._client = hub
      await SignalRService._client.StartConnection()
    }

    return SignalRService._client
  }

  private constructor() {
    this._connection = new SignalRClient.HubConnectionBuilder()
      .withUrl(`${baseUrls.messageHub}/live-updates`, {
        accessTokenFactory: GetAccessToken,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (document.hidden) {
            store.dispatch(
              SetConnectionStatus('disconnected', new Error('idle'))
            )
            document.addEventListener('visibilitychange', async () => {
              if (!document.hidden) {
                window.location.reload()
              }
            })
            return null
          }

          if (retryContext.previousRetryCount < 5) {
            const nextAttempt = Math.pow(2, retryContext.previousRetryCount + 1)
            store.dispatch(
              SetConnectionStatus(
                'reconnecting',
                retryContext.retryReason,
                `attempt ${
                  retryContext.previousRetryCount + 1
                }/5. Next attempt in ${nextAttempt}s`,
                retryContext.previousRetryCount + 1
              )
            )
            return nextAttempt * 1000
          } else {
            store.dispatch(
              SetConnectionStatus(
                'disconnected',
                retryContext.retryReason,
                null,
                retryContext.previousRetryCount + 1
              )
            )
            return null
          }
        },
      })
      .configureLogging(SignalRClient.LogLevel.Information)
      .build()

    this.StartConnection = this.StartConnection.bind(this)
    this.unregister = this.unregister.bind(this)
    this.Disconnect = this.Disconnect.bind(this)
    this.send = this.send.bind(this)
    this.registerHandler = this.registerHandler.bind(this)
    this.JoinGroup = this.JoinGroup.bind(this)
    this.LeaveGroup = this.LeaveGroup.bind(this)
    this.ReconnectToGroupsHandler = this.ReconnectToGroupsHandler.bind(this)
  }

  public async StartConnection() {
    try {
      if (
        this._connection.state !== SignalRClient.HubConnectionState.Disconnected
      ) {
        return
      }

      await this._connection.start()

      store.dispatch(SetConnectionStatus('online'))

      this._connection.onclose((err) => {
        Debugger.Log('info', '[signalR]: onClose', err)
        store.dispatch(SetConnectionStatus('disconnected', err))
      })

      this._connection.onreconnecting((err) => {
        Debugger.Log('info', '[signalR]: onReconnecting', err)
        store.dispatch(SetConnectionStatus('reconnecting', err))
      })
    } catch (err) {
      Debugger.Log('error', `Connection to ${this.hub} failed`, err)

      if (err instanceof Error) {
        store.dispatch(SetConnectionStatus('offline', err))
      }

      throw err
    }
  }

  public async Disconnect() {
    try {
      await this._connection.stop()
    } catch (err) {
      Debugger.Log('error', `Connection to ${this.hub} failed to stop`, err)
    }
  }

  public async send(methodName: string, ...args: unknown[]) {
    try {
      Debugger.Log('info', `[signalr:send]${methodName}`, args)

      let retryCount = 1

      if (retryCount > 3) {
        Debugger.Log(
          'error',
          `unable to send signalr messages when connection state is ${this._connection.state}`
        )
      }

      if (
        this._connection.state !== SignalRClient.HubConnectionState.Connected
      ) {
        setTimeout(() => {
          retryCount++
          Debugger.Log(
            'warning',
            `unable to send signalr messages when connection state is ${this._connection.state}}. Will retry in 2000ms. Attempt ${retryCount}`
          )
          this.send(methodName, args)
        }, 5000)
      } else {
        this._connection.send(methodName, ...args)
      }
    } catch (err) {
      Debugger.Log('error', `Unable to send a signalr message`, err)
      throw err
    }
  }

  private async ReconnectToGroupsHandler() {
    Debugger.Log(
      'warning',
      'reconnecting to groups',
      this._currentConnectedGroups
    )
    store.dispatch(SetConnectionStatus('online'))
    this._currentConnectedGroups.forEach(async (groupName) => {
      await this.JoinGroup(
        groupName,
        () => Debugger.Log('info', `reconnected to group ${groupName}`),
        true
      )
    })
  }

  public async JoinGroup(
    groupName: string,
    onEnterGroupHandler: () => void,
    force?: boolean
  ) {
    try {
      if (!force && this._currentConnectedGroups.includes(groupName)) {
        return
      }

      this.unregister(`onEnterGroup-${groupName}`)

      this.registerHandler(`onEnterGroup-${groupName}`, () => {
        store.dispatch(AddGroup(groupName))
        onEnterGroupHandler && onEnterGroupHandler()
      })

      console.info('asking to join project update group', groupName)
      await this.send('JoinGroup', groupName)
      this._currentConnectedGroups = _.uniq([
        ...this._currentConnectedGroups,
        groupName,
      ])
      this._connection.onreconnected(() => this.ReconnectToGroupsHandler())
    } catch (ex) {
      ShowException('signalr', ex)
    }
  }

  public async LeaveGroup(groupName: string, onLeaveGroupHandler: () => void) {
    this._currentConnectedGroups = this._currentConnectedGroups.filter(
      (group) => groupName !== group
    )

    this.registerHandler(`onLeaveGroup-${groupName}`, () => {
      store.dispatch(RemoveGroup(groupName))
      onLeaveGroupHandler && onLeaveGroupHandler()
      this.unregister(`onEnterGroup-${groupName}`)
      this.unregister(`onLeaveGroup-${groupName}`)
    })

    await this.send('LeaveGroup', groupName)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public registerHandler(methodName: string, action: (...args: any[]) => void) {
    this._connection.on(methodName, action)
    return this
  }

  public unregister(methodName: string) {
    this._connection.off(methodName)
  }
}
